// ============================================================================
//  Cliente de /topicos — el explorador de tópicos en vivo (ver
//  socket/topic-explorer.ts y specs/20260904-01-…). Pantalla de campo para
//  DESCUBRIR el tópico real de un equipo y copiarlo a /config, en vez de
//  escribirlo de memoria.
//
//  Es el primer cliente del repo que EMITE por Socket.IO (los demás solo
//  escuchan). Sigue sin mutar nada: los tres eventos abren una vista de solo
//  lectura sobre el caché de diagnóstico.
// ============================================================================
import type {
  DiscoveredFan,
  DiscoveredGasSensor,
  DiscoveredStatusLight,
  DiscoveredTopic,
  PortalRow,
  PortalTopicDiscovery,
  TopicDiscoveryError,
  TopicDiscoverySubscribed,
  TopicDiscoveryUnsubscribed,
  TopicEntry,
  TopicExplorerBatch,
  TopicExplorerError,
  TopicExplorerSubscribed,
  TopicExplorerUnsubscribed,
} from '../../src/shared/types';
import { esc, getJson, toast } from '../shared/api';

interface ExplorerSocket {
  on(event: string, cb: (data: never) => void): void;
  emit(event: string, arg?: unknown): void;
}
declare const io: (() => ExplorerSocket) | undefined;

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function fmtAgo(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return `hace ${s} s`;
  if (s < 5400) return `hace ${Math.round(s / 60)} min`;
  return `hace ${Math.round(s / 3600)} h`;
}

// El sistema clasifica cada tópico contra la configuración vigente. 'unknown'
// —llega pero no está asignado a nada— es justo lo que el operador vino a
// buscar, así que se resalta en vez de esconderse.
const KIND_LABEL: Record<string, string> = {
  unknown: 'sin asignar',
  gas: 'gas',
  vfd: 'variador',
  readerEvent: 'lector',
  readerStatus: 'lector',
};

function kindPill(e: TopicEntry): string {
  const label = KIND_LABEL[e.kind] ?? e.kind;
  const detail =
    e.kind === 'gas'
      ? `${e.variable}${e.portal ? ` · ${e.portal}` : ''}`
      : e.kind === 'vfd'
        ? `${e.variable}${e.fanId !== null ? ` · vent. ${e.fanId}` : ''}`
        : e.kind === 'readerEvent' || e.kind === 'readerStatus'
          ? e.portal || 'sin bocamina'
          : '';
  const cls = e.kind === 'unknown' ? 'pill pill-warn' : 'pill pill-ok';
  return `<span class="${cls}">${esc(label)}</span>${detail ? `<span class="tp-detail">${esc(detail)}</span>` : ''}`;
}

/** El valor numérico solo existe para gas/vfd; para todo lo demás —y es el
 *  caso del que el operador está descubriendo— lo útil es el payload crudo. */
function valueCell(e: TopicEntry): string {
  const shown = e.value !== null ? String(e.value) : e.payload;
  const clipped = shown.length > 80 ? `${shown.slice(0, 80)}…` : shown;
  return `<span class="mono">${esc(clipped)}</span>${
    e.retained ? '<span class="tp-detail">retenido</span>' : ''
  }`;
}

// ==========================================================================
//  Estado de la pantalla
// ==========================================================================
let last: TopicExplorerBatch | null = null;
let onlyUnknown = false;
/** Lo último que el operador pidió: se reenvía en cada 'connect', que es lo
 *  que cubre el reconnect automático de Socket.IO (el servidor no reenvía
 *  nada al conectar — no hay filtro que reenviar hasta que se pida uno). */
let pattern = '#';
let keepAlive: ReturnType<typeof setInterval> | null = null;

function render(): void {
  const body = $('tp-rows');
  const empty = $('tp-empty');
  const count = $('tp-count');
  if (!body || !empty || !count) return;

  const rows = (last?.topics ?? []).filter((e) => !onlyUnknown || e.kind === 'unknown');
  const now = Date.now() / 1000;

  body.innerHTML = rows
    .map(
      (e) => `<tr>
        <td class="mono tp-topic">${esc(e.topic)}</td>
        <td>${kindPill(e)}</td>
        <td>${valueCell(e)}</td>
        <td>${esc(fmtAgo(now - e.ts))}</td>
        <td>${e.count}</td>
        <td class="row-actions"><button class="btn btn-sm btn-ghost" data-copy="${esc(e.topic)}" type="button">Copiar</button></td>
      </tr>`,
    )
    .join('');

  empty.hidden = rows.length > 0;
  if (!rows.length) {
    empty.textContent = last
      ? onlyUnknown
        ? 'Ninguno sin asignar con este filtro — todo lo que llega ya está configurado.'
        : 'Ningún tópico coincide con este filtro. Revisá el filtro efectivo de arriba.'
      : 'Esperando el primer mensaje…';
  }

  count.textContent = last
    ? `${rows.length}${rows.length !== last.total ? ` de ${last.total}` : ''}`
    : '';
}

function renderStatus(): void {
  const el = $('tp-status');
  if (!el) return;
  if (!last) {
    el.innerHTML = '';
    return;
  }
  // El filtro EFECTIVO es lo que explica una lista vacía: si el operador
  // escribió algo que se ancló distinto de lo que imaginaba, acá se ve.
  el.innerHTML =
    `<span>Filtro efectivo: <code>${esc(last.pattern)}</code></span>` +
    `<span>${last.total} tópico${last.total === 1 ? '' : 's'}</span>` +
    (last.truncated
      ? `<span class="tp-warn">lista recortada a ${last.topics.length} — afiná el filtro</span>`
      : '');
}

function setConn(text: string, ok: boolean): void {
  const el = $('tp-conn');
  if (!el) return;
  el.textContent = text;
  el.className = ok ? 'tp-conn tp-conn-ok' : 'tp-conn';
}

function showError(message: string): void {
  const el = $('tp-error');
  if (!el) return;
  el.innerHTML = `<p>${esc(message)}</p>`;
  el.hidden = false;
}

function clearError(): void {
  const el = $('tp-error');
  if (el) el.hidden = true;
}

// ==========================================================================
//  Descubrimiento por bocamina (ver socket/portal-discovery.ts)
// ==========================================================================
let discovery: PortalTopicDiscovery | null = null;
let discoveryPortalId: number | null = null;
let discoveryKeepAlive: ReturnType<typeof setInterval> | null = null;

/** Los siete campos de FanConfig, con su nombre en español y el del campo:
 *  el objetivo de la pantalla es que el operador sepa QUÉ pegar y DÓNDE. */
const FAN_FIELDS: Array<[keyof DiscoveredFan, string]> = [
  ['commandTopic', 'Consigna (Hz) — escritura'],
  ['statusTopic', 'Encendido/apagado — escritura'],
  ['feedbackTopic', 'Frecuencia reportada'],
  ['statusFeedbackTopic', 'Estado reportado'],
  ['voltageTopic', 'Voltaje'],
  ['currentTopic', 'Corriente'],
  ['powerTopic', 'Potencia'],
];

function copyBtn(topic: string): string {
  return `<button class="btn btn-sm btn-ghost" data-copy="${esc(topic)}" type="button">Copiar</button>`;
}

function assignedPill(assigned: boolean, assignedTo: string): string {
  return assigned
    ? `<span class="pill pill-ok">ya configurado</span><span class="tp-detail">${esc(assignedTo)}</span>`
    : '';
}

function gasCard(sensor: DiscoveredGasSensor): string {
  const rows = sensor.gases
    .map(
      (g) => `<tr>
        <td>${esc(g.variable)} <span class="tp-detail">${esc(g.unit)}</span></td>
        <td class="mono tp-topic">${esc(g.topic)}</td>
        <td>${g.value !== null ? esc(String(g.value)) : '—'}</td>
        <td>${assignedPill(g.assigned, g.assignedTo)}</td>
        <td class="row-actions">${copyBtn(g.topic)}</td>
      </tr>`,
    )
    .join('');
  const missing = sensor.missing.length
    ? `<p class="lede">No publica: <strong>${esc(sensor.missing.join(', '))}</strong>.</p>`
    : '';
  return `<h3>Gases${sensor.group ? ` — <code>${esc(sensor.group)}</code>` : ''}</h3>
    ${missing}
    <div class="table-wrap"><table class="list">
      <thead><tr><th>Gas</th><th>Tópico</th><th>Último valor</th><th></th><th></th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
}

function fanCard(fan: DiscoveredFan): string {
  const rows = FAN_FIELDS.map(([field, label]) => {
    const topic = fan[field] as string;
    // los dos de escritura salen DERIVADOS por convención: nadie los publica,
    // así que no se pueden descubrir. Se marcan para que el operador los
    // confirme contra el variador en vez de darlos por vistos.
    const derived = fan.writeTopicsDerived && (field === 'commandTopic' || field === 'statusTopic');
    return `<tr>
      <td>${esc(label)}</td>
      <td class="mono tp-topic">${topic ? esc(topic) : '<span class="tp-detail">no se encontró</span>'}${
        derived ? '<span class="tp-detail">deducido</span>' : ''
      }</td>
      <td class="row-actions">${topic ? copyBtn(topic) : ''}</td>
    </tr>`;
  }).join('');
  const extra = fan.additionalTopics
    .map(
      (a) => `<tr>
        <td>Parámetro extra <code>${esc(a.parameter)}</code></td>
        <td class="mono tp-topic">${esc(a.topic)}</td>
        <td class="row-actions">${copyBtn(a.topic)}</td>
      </tr>`,
    )
    .join('');
  const warnings = fan.warnings.length
    ? `<div class="out-of-root">${fan.warnings.map((w) => `<p>${esc(w)}</p>`).join('')}</div>`
    : '';
  const assigned =
    fan.assignedFanId !== null
      ? `<span class="pill pill-ok">ya es el ventilador ${fan.assignedFanId}</span>`
      : '';
  return `<h3>Ventilador <code>${esc(fan.group || fan.suggestedName)}</code> ${assigned}</h3>
    ${warnings}
    <div class="table-wrap"><table class="list">
      <thead><tr><th>Campo</th><th>Tópico</th><th></th></tr></thead>
      <tbody>${rows}${extra}</tbody>
    </table></div>`;
}

/** El semáforo: lo que se configura es el de ESCRITURA, derivado del color
 *  que el LED reporta. Ver DiscoveredStatusLight. */
function ledCard(leds: DiscoveredStatusLight[]): string {
  if (!leds.length) return '';
  return `<h3>Semáforos</h3>
    <div class="table-wrap"><table class="list">
      <thead><tr><th>Equipo</th><th>Tópico a configurar (escritura)</th><th>Color que reporta</th><th></th></tr></thead>
      <tbody>${leds
        .map(
          (l) => `<tr>
            <td>${esc(l.suggestedName)}</td>
            <td class="mono tp-topic">${esc(l.topic)}${
              l.writeTopicDerived ? '<span class="tp-detail">deducido</span>' : ''
            }</td>
            <td>${l.value !== null ? esc(String(l.value)) : esc(l.payload)}</td>
            <td class="row-actions">${copyBtn(l.topic)}</td>
          </tr>`,
        )
        .join('')}</tbody>
    </table></div>`;
}

/** Lo que llegó y no se pudo proponer, CON el motivo: es lo que le dice al
 *  operador qué renombrar en el equipo. */
function unmatchedCard(rows: DiscoveredTopic[]): string {
  if (!rows.length) return '';
  return `<h3>Sin clasificar</h3>
    <div class="table-wrap"><table class="list">
      <thead><tr><th>Tópico</th><th>Motivo</th><th></th></tr></thead>
      <tbody>${rows
        .map(
          (t) => `<tr>
            <td class="mono tp-topic">${esc(t.topic)}</td>
            <td>${esc(t.note)}</td>
            <td class="row-actions">${copyBtn(t.topic)}</td>
          </tr>`,
        )
        .join('')}</tbody>
    </table></div>`;
}

function renderDiscovery(): void {
  const out = $('dc-out');
  const status = $('dc-status');
  if (!out || !status) return;
  if (!discovery) {
    out.innerHTML = '';
    status.textContent = '';
    return;
  }
  const d = discovery;
  // el conteo de callados es la diferencia entre "ese equipo nunca existió" y
  // "ese equipo se calló", que es lo que el operador necesita saber.
  const callados = d.staleCount
    ? ` · ${d.staleCount} sin publicar hace más de ${d.maxAgeSeconds} s`
    : '';
  status.textContent = `${d.total} tópico${d.total === 1 ? '' : 's'} bajo ${d.prefix}${callados}`;
  const parts = [
    ...d.gasSensors.map(gasCard),
    ...d.fans.map(fanCard),
    ledCard(d.statusLights),
    unmatchedCard(d.unmatched),
  ].filter(Boolean);
  out.innerHTML = parts.length
    ? parts.join('')
    : `<p class="empty">Nada publicando bajo <code>${esc(d.prefix)}</code> en los últimos ${d.maxAgeSeconds} s${
        d.staleCount
          ? ` (${d.staleCount} tópico${d.staleCount === 1 ? '' : 's'} llegó antes y se calló)`
          : ''
      }. Pedí que enciendan los equipos: van a aparecer solos.</p>`;
}

async function loadPortals(): Promise<void> {
  const sel = $('dc-portal') as HTMLSelectElement | null;
  if (!sel) return;
  try {
    const payload = await getJson<{ portals: PortalRow[] }>('/api/settings');
    sel.innerHTML = payload.portals
      .map((p) => `<option value="${p.id}">${esc(p.name)}</option>`)
      .join('');
  } catch {
    sel.innerHTML = '<option value="">— sin bocaminas —</option>';
  }
}

// ==========================================================================
//  Socket
// ==========================================================================
function start(): void {
  if (typeof io !== 'function') return;
  const socket = io();

  const apply = (raw: unknown): void => {
    last = raw as TopicExplorerBatch;
    clearError();
    renderStatus();
    render();
  };

  const subscribeDiscovery = (): void => {
    if (discoveryPortalId !== null) {
      socket.emit('subscribe-portal-discovery', { portalId: discoveryPortalId });
    }
  };

  socket.on('connect', () => {
    setConn('conectado', true);
    // suscribirse acá cubre el arranque Y el reconnect de una sola vez.
    socket.emit('subscribe-custom-mqtt', pattern);
    subscribeDiscovery();
  });
  socket.on('disconnect', () => setConn('sin conexión', false));

  // snapshot y batch traen la misma forma: mismo handler.
  socket.on('custom-subscription-snapshot', apply);
  socket.on('custom-subscription-batch', apply);

  socket.on('custom-mqtt-subscription-success', (data: never) => {
    // el latido sale de lo que dice el servidor, no de un número escrito acá.
    const { inactivitySeconds } = data as unknown as TopicExplorerSubscribed;
    if (keepAlive) clearInterval(keepAlive);
    keepAlive = setInterval(
      () => socket.emit('keep-alive-custom-mqtt'),
      Math.max(5, inactivitySeconds / 2) * 1000,
    );
  });

  socket.on('custom-mqtt-unsubscribed', (data: never) => {
    const { reason } = data as unknown as TopicExplorerUnsubscribed;
    // la pantalla sigue abierta: si venció por inactividad, se reabre sola.
    if (reason === 'inactivity timeout') socket.emit('subscribe-custom-mqtt', pattern);
  });

  // --- descubrimiento por bocamina: canal aparte, keep-alive aparte --------
  const applyDiscovery = (raw: unknown): void => {
    discovery = raw as PortalTopicDiscovery;
    const el = $('dc-error');
    if (el) el.hidden = true;
    renderDiscovery();
  };
  socket.on('portal-discovery-snapshot', applyDiscovery);
  socket.on('portal-discovery-batch', applyDiscovery);

  socket.on('portal-discovery-success', (data: never) => {
    const { inactivitySeconds } = data as unknown as TopicDiscoverySubscribed;
    if (discoveryKeepAlive) clearInterval(discoveryKeepAlive);
    // el keep-alive del explorador NO renueva éste: son canales independientes.
    discoveryKeepAlive = setInterval(
      () => socket.emit('keep-alive-portal-discovery'),
      Math.max(5, inactivitySeconds / 2) * 1000,
    );
  });

  socket.on('portal-discovery-unsubscribed', (data: never) => {
    if ((data as unknown as TopicDiscoveryUnsubscribed).reason === 'inactivity timeout') {
      subscribeDiscovery();
    }
  });

  socket.on('portal-discovery-error', (data: never) => {
    const el = $('dc-error');
    if (!el) return;
    el.innerHTML = `<p>${esc((data as unknown as TopicDiscoveryError).message)}</p>`;
    el.hidden = false;
  });

  $('dc-apply')?.addEventListener('click', () => {
    const value = ($('dc-portal') as HTMLSelectElement | null)?.value ?? '';
    if (!value) return;
    discoveryPortalId = Number(value);
    subscribeDiscovery();
  });

  // mismo destino que el resto de la pantalla: pegar el tópico en /config.
  $('dc-out')?.addEventListener('click', (ev) => {
    const topic = (ev.target as HTMLElement).closest('[data-copy]')?.getAttribute('data-copy');
    if (!topic) return;
    void navigator.clipboard
      .writeText(topic)
      .then(() => toast('Tópico copiado'))
      .catch(() => toast('No se pudo copiar', true));
  });

  socket.on('mqtt-subscription-error', (data: never) => {
    // un filtro rechazado NO tumba la suscripción vigente: la lista anterior
    // sigue llegando, así que solo se muestra el aviso.
    showError((data as unknown as TopicExplorerError).message);
  });

  const input = $('tp-pattern') as HTMLInputElement | null;
  const send = (): void => {
    pattern = input?.value ?? '#';
    socket.emit('subscribe-custom-mqtt', pattern);
  };
  $('tp-apply')?.addEventListener('click', send);
  input?.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') send();
  });

  ($('tp-only-unknown') as HTMLInputElement | null)?.addEventListener('change', (ev) => {
    onlyUnknown = (ev.target as HTMLInputElement).checked;
    render();
  });

  // copiar: el objetivo de toda la pantalla es pegarlo en /config.
  $('tp-rows')?.addEventListener('click', (ev) => {
    const topic = (ev.target as HTMLElement).closest('[data-copy]')?.getAttribute('data-copy');
    if (!topic) return;
    void navigator.clipboard
      .writeText(topic)
      .then(() => toast('Tópico copiado'))
      .catch(() => toast('No se pudo copiar', true));
  });

  // Al salir de la pantalla el socket se cierra igual y el servidor limpia la
  // suscripción solo (cleanupSocket), así que esto no es imprescindible — se
  // manda de todos modos para cerrar prolijo y porque esta pantalla es la que
  // ejercita los tres eventos del explorador en campo.
  window.addEventListener('pagehide', () => {
    if (keepAlive) clearInterval(keepAlive);
    if (discoveryKeepAlive) clearInterval(discoveryKeepAlive);
    socket.emit('unsubscribe-custom-mqtt');
    socket.emit('unsubscribe-portal-discovery');
  });

  setConn('conectando…', false);
}

void loadPortals();
start();
